// frontend/src/pages/admin/ProductEditPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft, FaSave, FaUpload } from 'react-icons/fa';
import {
  listProductDetails,
  updateProduct,
  createProduct,
} from '../../actions/productActions';
import { fetchCategories } from '../../actions/categoryActions';
import {
  PRODUCT_UPDATE_RESET,
  PRODUCT_CREATE_RESET,
  PRODUCT_DETAILS_RESET,
} from '../../constants/productConstants';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import CategorySelector from '../../components/admin/CategorySelector';

const ProductEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [name, setName] = useState('');
  const [chineseName, setChineseName] = useState('');
  const [price, setPrice] = useState('');
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [alcoholPercentage, setAlcoholPercentage] = useState('');
  const [volume, setVolume] = useState('');
  const [origin, setOrigin] = useState('');
  const [year, setYear] = useState('');
  const [formInitialized, setFormInitialized] = useState(false);
  
  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;
  
  const productUpdate = useSelector((state) => state.productUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = productUpdate;
  
  const productCreate = useSelector((state) => state.productCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    product: createdProduct,
  } = productCreate;
  
  const categoryList = useSelector((state) => state.categoryList);
  const { categories } = categoryList;
  
  const isEditMode = id && id !== 'new';
  
  // 重置表单数据的函数
  const resetForm = useCallback(() => {
    setName('');
    setChineseName('');
    setPrice('');
    setImage(null);
    setImageUrl('');
    setDescription('');
    setCategoryId('');
    setAlcoholPercentage('');
    setVolume('');
    setOrigin('');
    setYear('');
    setFormInitialized(false);
  }, []);
  
  // 填充表单数据的函数
  const populateForm = useCallback((productData) => {
    if (!productData || !productData.id || formInitialized) return;
    
    setName(productData.name || '');
    setChineseName(productData.chinese_name || '');
    setPrice(productData.price || '');
    setImageUrl(productData.image_url || '');
    setDescription(productData.description || '');
    setCategoryId(productData.category_id || '');
    setAlcoholPercentage(productData.alcohol_percentage || '');
    setVolume(productData.volume || '');
    setOrigin(productData.origin || '');
    setYear(productData.year || '');
    setFormInitialized(true);
  }, [formInitialized]);
  
  // 清理状态的 useEffect
  useEffect(() => {
    return () => {
      dispatch({ type: PRODUCT_UPDATE_RESET });
      dispatch({ type: PRODUCT_CREATE_RESET });
      dispatch({ type: PRODUCT_DETAILS_RESET });
    };
  }, [dispatch]);
  
  // 获取分类数据
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);
  
  // 处理成功状态
  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: PRODUCT_UPDATE_RESET });
      dispatch({ type: PRODUCT_DETAILS_RESET });
      navigate('/admin/products');
    }
    
    if (successCreate && createdProduct) {
      dispatch({ type: PRODUCT_CREATE_RESET });
      navigate(`/admin/product/${createdProduct.id}`);
    }
  }, [successUpdate, successCreate, createdProduct, navigate, dispatch]);
  
  // 获取产品详情（仅在编辑模式下执行一次）
  useEffect(() => {
    if (isEditMode && !formInitialized) {
      dispatch(listProductDetails(id));
    } else if (!isEditMode) {
      resetForm();
    }
  }, [dispatch, id, isEditMode, formInitialized, resetForm]);
  
  // 填充表单数据（当产品数据加载完成且表单未初始化时）
  useEffect(() => {
    if (isEditMode && product && product.id === parseInt(id) && !formInitialized) {
      populateForm(product);
    }
  }, [isEditMode, product, id, formInitialized, populateForm]);
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImageUrl(URL.createObjectURL(file));
    }
  };
  
  const submitHandler = async (e) => {
    e.preventDefault();
    
    // Client-side validation - 中文名称为必填
    if (!chineseName || !price) {
      return;
    }
    
    // Create FormData object for file uploads
    const formData = new FormData();
    formData.append('name', name || ''); // 英文名称可选
    formData.append('chinese_name', chineseName);
    formData.append('price', price);
    if (image) {
      formData.append('image', image);
    }
    formData.append('description', description);
    if (categoryId) {
      formData.append('category_id', categoryId);
    }
    if (alcoholPercentage) {
      formData.append('alcohol_percentage', alcoholPercentage);
    }
    if (volume) {
      formData.append('volume', volume);
    }
    if (origin) {
      formData.append('origin', origin);
    }
    if (year) {
      formData.append('year', year);
    }
    
    if (isEditMode) {
      dispatch(updateProduct(id, formData));
    } else {
      try {
        const result = await dispatch(createProduct(formData));
        if (result && result.id) {
          navigate(`/admin/product/${result.id}`);
        } else {
          navigate('/admin/products');
        }
      } catch (error) {
        console.error('Error creating product:', error);
      }
    }
  };
  
  return (
    <div className="product-edit-page">
      <div className="mb-4">
        <Link
          to="/admin/products"
          className="flex items-center text-gray-600 hover:text-red-700"
        >
          <FaArrowLeft className="mr-1" />
          <span>返回产品列表</span>
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold mb-6">
        {isEditMode ? '编辑产品' : '添加产品'}
      </h1>
      
      {loadingUpdate || loadingCreate ? (
        <Loader />
      ) : (
        <>
          {errorUpdate && <Message variant="error">{errorUpdate}</Message>}
          {errorCreate && <Message variant="error">{errorCreate}</Message>}
          
          {loading && isEditMode ? (
            <Loader />
          ) : error && isEditMode ? (
            <Message variant="error">{error}</Message>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <form onSubmit={submitHandler}>
                <div className="md:flex">
                  <div className="md:w-1/3 p-6 border-r">
                    <div className="mb-6">
                      <label className="block text-gray-700 mb-2">产品图片</label>
                      <div className="mb-4">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="Product"
                            className="w-full h-64 object-cover rounded"
                          />
                        ) : (
                          <div className="w-full h-64 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-gray-500">暂无图片</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="w-full bg-gray-100 text-gray-800 py-2 px-4 rounded border flex items-center justify-center hover:bg-gray-200 cursor-pointer"
                        >
                          <FaUpload className="mr-2" />
                          选择图片
                        </label>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">分类</label>
                      <CategorySelector
                        categories={categories || []}
                        selectedCategoryId={categoryId}
                        onCategoryChange={(id) => setCategoryId(id)}
                        placeholder="选择分类"
                        allowEmpty={true}
                      />
                    </div>
                  </div>
                  
                  <div className="md:w-2/3 p-6">
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">产品名称 *</label>
                      <input
                        type="text"
                        value={chineseName}
                        onChange={(e) => setChineseName(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="请输入产品中文名称"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">英文名称 (可选)</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="可选的英文名称"
                      />
                    </div>
                    
                    <div className="flex gap-4 mb-4">
                      <div className="w-1/2">
                        <label className="block text-gray-700 mb-2">价格 (¥) *</label>
                        <input
                          type="number"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          className="w-full p-2 border rounded"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      
                      <div className="w-1/2">
                        <label className="block text-gray-700 mb-2">酒精度数 (%)</label>
                        <input
                          type="number"
                          value={alcoholPercentage}
                          onChange={(e) => setAlcoholPercentage(e.target.value)}
                          className="w-full p-2 border rounded"
                          min="0"
                          step="0.1"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-4 mb-4">
                      <div className="w-1/3">
                        <label className="block text-gray-700 mb-2">容量 (ml)</label>
                        <input
                          type="number"
                          value={volume}
                          onChange={(e) => setVolume(e.target.value)}
                          className="w-full p-2 border rounded"
                          min="0"
                        />
                      </div>
                      
                      <div className="w-1/3">
                        <label className="block text-gray-700 mb-2">产地</label>
                        <input
                          type="text"
                          value={origin}
                          onChange={(e) => setOrigin(e.target.value)}
                          className="w-full p-2 border rounded"
                        />
                      </div>
                      
                      <div className="w-1/3">
                        <label className="block text-gray-700 mb-2">年份</label>
                        <input
                          type="number"
                          value={year}
                          onChange={(e) => setYear(e.target.value)}
                          className="w-full p-2 border rounded"
                          min="1900"
                          max={new Date().getFullYear()}
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-gray-700 mb-2">产品描述</label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full p-2 border rounded"
                        rows={6}
                        placeholder="请输入产品详细描述"
                      />
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-green-600 text-white px-6 py-2 rounded flex items-center hover:bg-green-700"
                      >
                        <FaSave className="mr-2" />
                        {isEditMode ? '保存更改' : '添加产品'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductEditPage;